import Axios from "../config/axios-v1-config";
import { apiErrorMessage } from "../utils/apiErrorMessage";

// Roles y permisos del operador logístico (R3.2). Rutas homeadas en
// microservice-users bajo /users/roles/* y /users/logistic-team/:id/role; el
// BFF solo las proxea. El operador sale del JWT en el backend: NUNCA se manda
// desde acá. Ninguna función lanza: devuelven `{ data, error }` como el resto
// de api/ del equipo interno, con el error ya normalizado (ver normalizeRolesError).

/** Códigos de error de negocio del contrato. */
export const ROLES_ERROR_CODES = {
  PERMISSION_NOT_GRANTABLE: "PERMISSION_NOT_GRANTABLE",
  ROLE_RANK_FORBIDDEN: "ROLE_RANK_FORBIDDEN",
  ROLE_NAME_TAKEN: "ROLE_NAME_TAKEN",
  ROLE_IN_USE: "ROLE_IN_USE",
  CUSTOM_ROLE_BASE_MISMATCH: "CUSTOM_ROLE_BASE_MISMATCH",
  SELF_ROLE_CHANGE: "SELF_ROLE_CHANGE",
  ROLES_NOT_AVAILABLE: "ROLES_NOT_AVAILABLE",
  FORBIDDEN: "FORBIDDEN",
  USER_NOT_FOUND: "USER_NOT_FOUND",
};

/** Máximo de claves que se listan en el mensaje de PERMISSION_NOT_GRANTABLE. */
const MAX_LISTED_PERMISSIONS = 8;

const stringList = (value) =>
  Array.isArray(value) ? value.filter((x) => typeof x === "string" && x) : [];

/**
 * Error del backend en la forma que consume la UI de roles.
 *
 * El body del contrato es `{ statusCode, code, message }`; además
 * PERMISSION_NOT_GRANTABLE trae la lista de claves rechazadas (se lee de
 * `permissions`, con `notGrantable` y `keys` como alias tolerados) y
 * ROLE_IN_USE trae `userCount`. El body no se descarta: se conserva tal cual.
 *
 * @param {unknown} error error de axios
 * @returns {{ status: number|null, code: string|null, message: string|null,
 *   permissions: string[], userCount: number|null }}
 */
export function normalizeRolesError(error) {
  const body = error?.response?.data;
  const base = body && typeof body === "object" && !Array.isArray(body) ? body : {};
  const userCount = Number(base.userCount);
  return {
    ...base,
    status: typeof error?.response?.status === "number" ? error.response.status : null,
    code: typeof base.code === "string" ? base.code : null,
    message: apiErrorMessage(error),
    permissions: stringList(base.permissions ?? base.notGrantable ?? base.keys),
    userCount: Number.isFinite(userCount) ? userCount : null,
  };
}

/**
 * Texto accionable para el usuario a partir de un error normalizado. Los
 * códigos conocidos tienen mensaje propio y traducido (el texto del backend
 * puede venir en inglés); cualquier otro cae al mensaje del backend si es
 * mostrable y, si no, al `fallback` ya traducido que pasa quien llama.
 *
 * @param {ReturnType<typeof normalizeRolesError>|null|undefined} error
 * @param {(key: string, opts?: object) => string} t
 * @param {string} fallback texto ya traducido para errores sin mensaje
 * @returns {string}
 */
export function rolesErrorMessage(error, t, fallback) {
  switch (error?.code) {
    case ROLES_ERROR_CODES.PERMISSION_NOT_GRANTABLE: {
      const keys = error.permissions || [];
      if (keys.length === 0) {
        return t("No puedes otorgar uno o más de los permisos seleccionados. Desmárcalos e intenta de nuevo.");
      }
      const shown = keys.slice(0, MAX_LISTED_PERMISSIONS).join(", ");
      const list = keys.length > MAX_LISTED_PERMISSIONS ? `${shown}…` : shown;
      return t("No puedes otorgar estos permisos: {{list}}. Desmárcalos e intenta de nuevo.", { list });
    }
    case ROLES_ERROR_CODES.ROLE_RANK_FORBIDDEN:
      return t("Tu rol no alcanza para administrar un rol de este nivel.");
    case ROLES_ERROR_CODES.ROLE_NAME_TAKEN:
      return t("Ya existe un rol con ese nombre en tu empresa. Elige otro nombre.");
    case ROLES_ERROR_CODES.ROLE_IN_USE:
      return error.userCount === null || error.userCount === undefined
        ? t("No se puede eliminar el rol porque todavía tiene usuarios asignados. Reasígnalos antes de eliminarlo.")
        : t("No se puede eliminar el rol porque todavía tiene usuarios asignados (usuarios: {{n}}). Reasígnalos antes de eliminarlo.", { n: error.userCount });
    case ROLES_ERROR_CODES.CUSTOM_ROLE_BASE_MISMATCH:
      return t("El rol propio elegido no corresponde al rol base seleccionado.");
    case ROLES_ERROR_CODES.SELF_ROLE_CHANGE:
      return t("No puedes cambiar tu propio rol.");
    case ROLES_ERROR_CODES.ROLES_NOT_AVAILABLE:
      return t("La gestión de roles no está disponible para este tipo de cuenta.");
    case ROLES_ERROR_CODES.FORBIDDEN:
      return t("No tienes permiso para realizar esta acción.");
    case ROLES_ERROR_CODES.USER_NOT_FOUND:
      return t("No encontramos a ese usuario en tu equipo.");
    default:
      return error?.message || fallback;
  }
}

const request = async (fn) => {
  try {
    const response = await fn();
    return { data: response.data ?? null, error: null };
  } catch (error) {
    return { data: null, error: normalizeRolesError(error) };
  }
};

/** Roles base gestionables, personalizados (override) y propios del operador, con permisos efectivos. */
export const getRoles = () => request(() => Axios.get("/users/roles"));

/** Catálogo de permisos por módulo → recurso → acción; `grantable` ya considera al que llama. */
export const getRolesCatalog = () => request(() => Axios.get("/users/roles/catalog"));

/** @param {{ name: string, baseRoleId: string, description?: string, permissions: string[] }} payload */
export const createRole = (payload) => request(() => Axios.post("/users/roles", payload));

/** Solo roles propios. @param {{ name?: string, description?: string, permissions?: string[] }} payload */
export const updateRole = (roleId, payload) =>
  request(() => Axios.patch(`/users/roles/${encodeURIComponent(roleId)}`, payload));

/** Solo roles propios; 409 ROLE_IN_USE si tienen usuarios. */
export const deleteRole = (roleId) =>
  request(() => Axios.delete(`/users/roles/${encodeURIComponent(roleId)}`));

/** Crea o reemplaza la personalización de un rol base. @param {string[]} permissions */
export const putBaseRoleOverride = (baseRoleId, permissions) =>
  request(() =>
    Axios.put(`/users/roles/base/${encodeURIComponent(baseRoleId)}/override`, { permissions }),
  );

/** Vuelve el rol base a la plantilla global. */
export const deleteBaseRoleOverride = (baseRoleId) =>
  request(() => Axios.delete(`/users/roles/base/${encodeURIComponent(baseRoleId)}/override`));

/**
 * Cambia el rol de un usuario del equipo interno.
 * @param {string} userId
 * @param {{ baseRoleId: string, customRoleId?: string|null }} payload
 */
export const changeTeamUserRole = (userId, payload) =>
  request(() => Axios.patch(`/users/logistic-team/${encodeURIComponent(userId)}/role`, payload));
